import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bike, Car, Hammer, GraduationCap, Gift, HeartPulse, Monitor, Plane, Banknote, ArrowRight, ArrowLeft, Check } from 'lucide-react';
import { useApp } from '@/store/appContext';
import KompaLogo from '@/modules/shared/KompaLogo';
import StepIndicator from '@/modules/shared/StepIndicator';

const ICON_MAP = { Bike, Car, Hammer, GraduationCap, Gift, HeartPulse, Monitor, Plane, Banknote };
const SPEED = 10;

export default function CategorySelector() {
  const { categories, selectedCategory, selectCategory, setCurrentStep, user, isAddingGoal, setIsAddingGoal } = useApp();
  const [rotOffset, setRotOffset] = useState(0);
  const rafRef = useRef(null);

  const isRotating = selectedCategory === null;
  const radius = 120;
  const centerSize = 80;
  const nodeSize = 64;

  // Continuous clockwise rotation when nothing selected
  useEffect(() => {
    if (!isRotating) return;
    let prevTs = 0;
    const tick = (ts) => {
      if (prevTs) {
        const dt = Math.min((ts - prevTs) / 1000, 0.1);
        setRotOffset(prev => prev + dt * SPEED);
      }
      prevTs = ts;
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [isRotating]);

  const handleSelect = (catId, index) => {
    const angleStep = 360 / categories.length;
    if (selectedCategory === catId) {
      selectCategory(catId);
    } else {
      const rawAngle = ((index * angleStep + rotOffset) % 360 + 360) % 360;
      let delta = -rawAngle;
      if (delta < -180) delta += 360;
      if (delta > 180) delta -= 360;
      selectCategory(catId);
      setRotOffset(prev => prev + delta);
    }
  };

  const getNodePosition = (index) => {
    const angleStep = 360 / categories.length;
    const angleDeg = index * angleStep + rotOffset - 90;
    const angleRad = (angleDeg * Math.PI) / 180;
    return {
      x: Math.cos(angleRad) * radius,
      y: Math.sin(angleRad) * radius,
    };
  };

  const hasSelection = selectedCategory !== null;
  const selectedCat = categories.find(c => c.id === selectedCategory);

  return (
    <div className="min-h-screen flex flex-col bg-white px-6 md:px-8 pt-10 md:pt-14 pb-8 md:pb-10 max-w-2xl mx-auto w-full">
      <div className="flex items-center justify-between mb-2">
        <button
          onClick={() => {
            if (isAddingGoal) {
              setIsAddingGoal(false);
              setCurrentStep(11);
            } else {
              setCurrentStep(1);
            }
          }}
          className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
          data-testid="category-back-button"
        >
          <ArrowLeft className="w-5 h-5 text-black" />
        </button>
        <KompaLogo size="sm" />
        <div className="w-10" />
      </div>

      <StepIndicator current={1} />

      <div className="text-center mb-4 mt-2">
        <h1 className="text-2xl md:text-3xl font-bold text-black tracking-tight" data-testid="category-title">
          Elige tu meta
        </h1>
        <p className="text-gray-500 font-light text-sm mt-1">
          Selecciona una categoría
        </p>
      </div>

      {/* Circular Selector */}
      <div
        className="relative mx-auto my-4 md:my-10 w-full max-w-full overflow-hidden"
        style={{ maxWidth: (radius + nodeSize) * 2, height: (radius + nodeSize) * 2, aspectRatio: '1/1' }}
        data-testid="circular-selector"
      >
        {/* Orbit path */}
        <div
          className="absolute rounded-full border border-dashed border-gray-200"
          style={{
            width: radius * 2,
            height: radius * 2,
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        />

        {/* Center - User avatar */}
        <div
          className="absolute z-20 rounded-full border-4 border-white shadow-xl overflow-hidden bg-gray-100"
          style={{
            width: centerSize,
            height: centerSize,
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
          }}
          data-testid="center-avatar"
        >
          {user?.avatar ? (
            <img
              src={user.avatar}
              alt="Tu perfil"
              className="w-full h-full object-cover"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-[#fbb040]/20">
              <span className="text-2xl font-bold text-[#1f2d5a]">
                {(user?.nombre || user?.email || 'K').trim().charAt(0).toUpperCase()}
              </span>
            </div>
          )}
        </div>

        {/* Category nodes */}
        {categories.map((cat, index) => {
          const pos = getNodePosition(index);
          const isSelected = selectedCategory === cat.id;
          const Icon = ICON_MAP[cat.icon];

          return (
            <motion.button
              key={cat.id}
              onClick={() => handleSelect(cat.id, index)}
              className={`absolute z-10 rounded-full flex flex-col items-center justify-center cursor-pointer transition-colors ${
                isSelected
                  ? 'bg-[#fff8e1] border-2 border-[#fbb040] shadow-lg'
                  : 'bg-white border border-gray-100 shadow-md hover:shadow-lg'
              }`}
              style={{
                width: nodeSize,
                height: nodeSize,
                left: '50%',
                top: '50%',
              }}
              animate={{
                x: pos.x - nodeSize / 2,
                y: pos.y - nodeSize / 2,
                scale: isSelected ? 1.15 : 1,
              }}
              transition={
                isRotating
                  ? { duration: 0 }
                  : { type: 'spring', stiffness: 180, damping: 22 }
              }
              whileTap={{ scale: 0.9 }}
              data-testid={`category-node-${cat.id}`}
              aria-label={cat.label}
            >
              {isSelected && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -top-1 -right-1 w-5 h-5 bg-[#fbb040] rounded-full flex items-center justify-center z-30"
                >
                  <Check className="w-3 h-3 text-black" strokeWidth={3} />
                </motion.div>
              )}
              {Icon && <Icon className={`w-6 h-6 ${isSelected ? 'text-[#fbb040]' : 'text-gray-600'}`} />}
            </motion.button>
          );
        })}
      </div>

      {/* Selected category label */}
      <div className="mt-2 min-h-[60px]">
        <AnimatePresence>
          {selectedCat && (
            <motion.div
              key={selectedCat.id}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="flex items-center gap-3 bg-[#fff8e1] rounded-xl px-4 py-3"
              data-testid={`selected-tag-${selectedCat.id}`}
            >
              <div className="w-8 h-8 bg-[#fbb040]/20 rounded-lg flex items-center justify-center">
                {React.createElement(ICON_MAP[selectedCat.icon], { className: 'w-4 h-4 text-[#fbb040]' })}
              </div>
              <div>
                <p className="text-sm font-bold text-black">{selectedCat.label}</p>
                <p className="text-xs font-light text-gray-600">{selectedCat.subtitle}</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Category legend */}
      <div className="grid grid-cols-3 gap-2 mt-4 mb-6">
        {categories.map((cat) => {
          const isSelected = selectedCategory === cat.id;
          const Icon = ICON_MAP[cat.icon];
          return (
            <button
              key={cat.id}
              onClick={() => handleSelect(cat.id, categories.indexOf(cat))}
              className={`flex flex-col items-center gap-1 py-2 px-1 rounded-xl text-center transition-colors ${
                isSelected ? 'bg-[#fff8e1]' : 'hover:bg-gray-50'
              }`}
              data-testid={`category-legend-${cat.id}`}
            >
              {Icon && <Icon className={`w-4 h-4 ${isSelected ? 'text-[#fbb040]' : 'text-gray-400'}`} />}
              <span className={`text-xs font-medium ${isSelected ? 'text-black' : 'text-gray-500'}`}>
                {cat.label}
              </span>
            </button>
          );
        })}
      </div>

      <div className="mt-auto">
        <motion.button
          onClick={() => setCurrentStep(3)}
          disabled={!hasSelection}
          whileTap={{ scale: 0.97 }}
          className={`w-full font-bold rounded-full h-14 flex items-center justify-center gap-2 transition-all text-lg ${
            hasSelection
              ? 'bg-[#fbb040] text-black hover:bg-[#ffa000]'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
          data-testid="category-next-button"
        >
          Siguiente
          <ArrowRight className="w-5 h-5" />
        </motion.button>
      </div>
    </div>
  );
}
